import { css, html, LitElement, nothing, PropertyValues, svg } from 'lit'
import { customElement, query, state } from 'lit/decorators.js'
import { IApiMediaWithCropsModel, VideoSettingsElementModel } from '../api/ContentDeliveryApi.schemas'
import { MEDIA_BASE } from '../Settings'
import { translate } from './Translations'
import Hls from 'hls.js'
import { styleMap } from 'lit/directives/style-map.js'

@customElement('lindberg-video')
export class Video extends LitElement {
    static styles = css`
        :host{
            display: block;
            width:100%;
            position: relative;
            grid-row: inherit;
            grid-column: inherit;
        }

        video {
            display: block;
            width:100%;
            /* height: 100%; */
            object-fit: cover;
            z-index:0;
            position:relative;
            border-radius: var(--border-radius);
            aspect-ratio: var(--aspect-ratio);
            grid-row: inherit;
            grid-column: inherit;
            &::cue {
                color: #fff;
                background: rgba(0, 0, 0, 0.25);
                font-family: inherit;
                font-size: var(--font-size-base-lg);
                line-height: 1.4;            
            }
        }

        button {
            position: absolute;
            display: flex;
            z-index: 1;
            background: rgba(0, 0, 0, 0.25);
            border: none;
            border-radius: 50%;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: white;
            align-items: center;
            justify-content: stretch;
            &.play {
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 200px;
                height: 200px;
            }
            &.audio {
                top: 10px;
                right: 10px;
                width: 40px;
                height: 40px;
            }
        }
    `

    private _data!: IApiMediaWithCropsModel
    public get data(): IApiMediaWithCropsModel {
        return this._data
    }

    public set data(value: IApiMediaWithCropsModel) {
        this._data = value
        this.hasAudio = !!value.properties.hasAudio
    }

    private _settings!: VideoSettingsElementModel
    public get settings(): VideoSettingsElementModel {
        return this._settings
    }

    public set settings(value: VideoSettingsElementModel) {
        this._settings = value
        this.autoplay = !!value.properties.autoplay
        this.loop = !!value.properties.loop
    }

    @query('video')
    video!: HTMLVideoElement

    @state()
    hasAudio = false

    @state()
    muted = false

    @state()
    autoplay = false

    @state()
    loop = false

    private hls?: Hls;
    private active = false;
    private srcPromise?: Promise<string>;
    private resizeObserver?: ResizeObserver;

    private static sharedEstimate?: number


    firstUpdated() {
        if (this.autoplay && this.hasAudio) this.muted = true;
        this.video.playsInline = true;
        this.video.crossOrigin = 'anonymous';

        this.resizeObserver = new ResizeObserver(([entry]) => {
            const visible = entry.contentRect.width > 0;
            if (visible && !this.active) this.init();
            else if (!visible && this.active) this.teardown();
        });
        this.resizeObserver.observe(this.video);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.resizeObserver?.disconnect();
        this.teardown();
    }

    // Fetch the HLS URL once, reuse it if the element is shown again
    private getSrc() {
        return (this.srcPromise ??= fetch(`${MEDIA_BASE}/umbraco/delivery/api/v2/extended/media/${this.data.id}/hls`)
            .then(res => res.json() as Promise<{ url: string }>)
            .then(({ url }) => url));
    }

    private async init() {
        this.active = true;
        const src = await this.getSrc();
        // Hidden again while fetching, or already started
        if (!this.active || this.hls) return;

        const label = this.data.name;

        if (Hls.isSupported()) {
            this.hls = new Hls({
                capLevelToPlayerSize: true,
                abrEwmaDefaultEstimate: Video.sharedEstimate ?? 8_000_000,
            });
            this.hls.subtitleDisplay = false;
            this.hls.on(Hls.Events.FRAG_LOADED, () => {
                Video.sharedEstimate = this.hls!.bandwidthEstimate;
            });
            this.hls.on(Hls.Events.LEVEL_SWITCHED, (_, { level }) => {
                const l = this.hls!.levels[level];
                console.log(`[${label}] hls.js: ${l.width}x${l.height} @ ${Math.round(l.bitrate / 1000)}kbps`);
            });
            this.hls.loadSource(src);
            this.hls.attachMedia(this.video);
        } else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
            this.video.src = src;
            this.video.textTracks.onchange = () => {
                Array.from(this.video.textTracks).forEach(track => {
                    if (track.language.includes('auto')) track.mode = 'hidden';
                });
            };
        } else {
            this.video.src = this.data.properties.url as string;
        }
    }

    private teardown() {
        this.active = false;
        this.hls?.destroy();
        this.hls = undefined;
        // Stop native/fallback playback and release the network connection
        this.video.removeAttribute('src');
        this.video.load();
    }

    startVideo(){
        if(this.video.paused) {
            this.video.play()
        }else if(!this.autoplay){
            this.video.pause()
        }else {
            return
        }
        this.requestUpdate()
    }

    render() {
        const aspectRatio = `${this.data.properties.width} / ${this.data.properties.height}`
        const color = this.data.properties.color as string | undefined ?? ''
        return html`
            <style>:host {--aspect-ratio: ${aspectRatio};}</style>
            ${this.data.properties.hasAudio ? html`
                <button type="button" class="audio" aria-label="${translate(this.muted ? 'video-unmute' : 'video-mute')}" @click="${() => this.muted = !this.muted}">
                    ${this.muted ? muteIcon : unmuteIcon}
                </button>
            ` : nothing}
            ${!this.autoplay && this.video?.paused !== false ? html`<button type="button" class="play" aria-label="${translate('video-play')}" @click="${() => this.startVideo()}">
                ${playIcon}
            </button>`: nothing}

            <video
                @click="${() => this.startVideo()}"
                style=${styleMap({ '--aspect-ratio': String(aspectRatio), backgroundColor: color })}
                ?autoplay=${this.autoplay}
                .muted=${this.muted || !this.data.properties.hasAudio}
                ?loop=${this.loop}>
            </video>
        `
    }
}


const muteIcon = svg`<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" fill="currentColor" x="0px" y="0px" viewBox="0 0 32 32">
<path class="st0" d="M2.2,4.2l28.5,22.5c0.3,0.2,0.4,0.8,0.1,1c-0.2,0.3-0.7,0.4-1,0.1L1.3,5.4C1,5.1,0.9,4.6,1.1,4.3
	C1.4,4,1.9,3.9,2.2,4.2z M28,16c0,1.7-0.5,3.3-1.3,4.6l-1.2-0.9c0.6-1.1,1-2.3,1-3.7c0-2.3-1.1-4.4-2.8-5.8
	c-0.3-0.3-0.3-0.8-0.1-1.1c0.3-0.3,0.8-0.3,1.1-0.1C26.7,10.7,28,13.2,28,16z M23.1,17.8l-1.3-1C22,16.6,22,16.3,22,16
	c0-0.9-0.5-1.8-1.2-2.4c-0.3-0.2-0.4-0.7-0.1-1c0.2-0.3,0.7-0.4,1-0.1c1.1,0.8,1.8,2.1,1.8,3.6C23.5,16.7,23.4,17.3,23.1,17.8z
	 M17.5,6.9v6.5L16,12.2V7.1l-3,2.7l-1.2-0.9l3.4-3c0.2-0.2,0.6-0.4,0.9-0.4C16.8,5.5,17.5,6.2,17.5,6.9z M5.1,13
	C4.5,13,4,13.5,4,14.1v3.7C4,18.5,4.5,19,5.1,19h4c0.1,0,0.3,0.1,0.5,0.2l6.4,5.7v-5.1l1.5,1.2v4.1c0,0.8-0.7,1.4-1.4,1.4
	c-0.4,0-0.7-0.1-0.9-0.3l0.5-0.6l-0.5,0.6l-6.4-5.7H5.1c-1.5,0-2.6-1.2-2.6-2.6v-3.7c0-1.4,1.2-2.6,2.6-2.6h0.3L7.3,13H5.1z"/>
</svg>
`
const unmuteIcon = svg`<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" fill="currentColor" x="0px" y="0px" viewBox="0 0 32 32">
<path class="st0" d="M16,7.1l-6.4,5.7C9.4,13,9.2,13,9.1,13h-4C4.5,13,4,13.5,4,14.1v3.7C4,18.5,4.5,19,5.1,19h4
	c0.1,0,0.3,0.1,0.5,0.2l6.4,5.7V7.1z M15.2,5.9c0.2-0.2,0.6-0.4,0.9-0.4c0.8,0,1.4,0.7,1.4,1.4v18.2c0,0.8-0.7,1.4-1.4,1.4
	c-0.4,0-0.7-0.1-0.9-0.3l0.5-0.6l-0.5,0.6l-6.4-5.7H5.1c-1.5,0-2.6-1.2-2.6-2.6v-3.7c0-1.4,1.2-2.6,2.6-2.6h3.7L15.2,5.9z
	 M20.6,12.6c0.2-0.3,0.7-0.4,1-0.1c1.1,0.8,1.8,2.1,1.8,3.6c0,1.5-0.8,2.8-1.8,3.6c-0.3,0.3-0.8,0.2-1-0.1c-0.3-0.3-0.2-0.8,0.1-1
	C21.5,17.9,22,17,22,16c0-0.9-0.5-1.8-1.2-2.4C20.5,13.4,20.4,12.9,20.6,12.6z M24.7,9.1c2,1.7,3.3,4.2,3.3,6.9c0,2.8-1.3,5.3-3.3,7
	c-0.3,0.2-0.8,0.2-1.1-0.1c-0.2-0.3-0.2-0.8,0.1-1c1.7-1.4,2.8-3.5,2.8-5.8c0-2.3-1.1-4.4-2.8-5.8c-0.3-0.3-0.3-0.8-0.1-1
	C23.9,8.9,24.4,8.8,24.7,9.1z"/>
</svg>
`

const playIcon = svg`
<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" fill="currentColor" class="bi bi-play-circle" viewBox="0 0 16 16">
  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
  <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z"/>
</svg>`